/**
 * Truncate a categorical label for tight spaces (axis ticks, legends);
 * full text stays available via tooltips/title attributes.
 */
export function truncateLabel(value: string, max = 14): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

/**
 * Short form of a tool id for chart series, legends and axis ticks. Ids up to 16 characters stay
 * whole ("HEARC-1520-0002", "Test Teams"); longer ones keep their first 6 and last 8 characters
 * around an ellipsis ("003203…00000041"), as long ids are serial codes that differ at the end.
 * Charts group by this value, so it must stay distinct for distinct tools.
 */
export function shortToolId(toolId: string): string {
  if (toolId.length <= 16) return toolId;
  return `${toolId.slice(0, 6)}…${toolId.slice(-8)}`;
}

/**
 * Format duration for axis ticks (shorter format)
 * @param seconds - Duration in seconds
 * @returns Short formatted string
 */
function formatDurationAxis(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '0';

  const minutes = seconds / 60;

  if (minutes < 1) {
    return `${seconds.toFixed(0)}s`;
  }

  if (minutes < 60) {
    return `${minutes.toFixed(0)}m`;
  }

  const hours = minutes / 60;
  return `${hours.toFixed(1)}h`;
}

/**
 * Format volume for axis ticks (shorter format)
 * @param cm3 - Volume in cubic centimeters
 * @returns Short formatted string
 */
function formatVolumeAxis(cm3: number | null): string {
  if (cm3 === null || cm3 === undefined) return '0';

  if (cm3 >= 10000) {
    return `${(cm3 / 1000).toFixed(1)}L`;
  }

  if (cm3 >= 1000) {
    return `${(cm3 / 1000).toFixed(1)}k`;
  }

  return `${cm3.toFixed(0)}`;
}

/**
 * Format distance for axis ticks (shorter format)
 * @param mm - Distance in millimeters
 * @returns Short formatted string
 */
function formatDistanceAxis(mm: number | null): string {
  if (mm === null || mm === undefined) return '0';

  if (mm >= 1000) {
    return `${(mm / 1000).toFixed(1)}m`;
  }

  return `${mm.toFixed(0)}mm`;
}

/**
 * Format malround for axis ticks (shorter format)
 * @param micrometers - Malround in micrometers
 * @returns Short formatted string
 */
function formatMalroundAxis(micrometers: number | null): string {
  if (micrometers === null || micrometers === undefined) return '0';

  return `${micrometers.toFixed(1)}µm`;
}

/**
 * Format pieces for axis ticks (shorter format)
 * @param count - Number of pieces
 * @returns Short formatted string
 */
function formatPiecesAxis(count: number | null): string {
  if (count === null || count === undefined) return '0';

  const rounded = Math.round(count);

  if (rounded >= 10000) {
    return `${(rounded / 1000).toFixed(1)}K`;
  }

  if (rounded >= 1000) {
    return `${(rounded / 1000).toFixed(1)}k`;
  }

  return `${rounded}`;
}

/**
 * Get the appropriate axis formatter for a metric
 * @param metric - Metric key
 * @returns Axis formatter function
 */
export function getMetricAxisFormatter(metric: string): (value: number | null) => string {
  switch (metric) {
    case 'totalMachiningDuration':
      return formatDurationAxis;
    case 'pieceMachiningDuration':
      return formatDurationAxis;
    case 'machinedVolume':
      return formatVolumeAxis;
    case 'machinedDistance':
      return formatDistanceAxis;
    case 'machinedPieces':
      return formatPiecesAxis;
    case 'malround':
      return formatMalroundAxis;
    case 'vc':
      return (value) => (value !== null ? value.toFixed(0) : '0');
    case 'fz':
      return (value) => (value !== null ? value.toFixed(3) : '0');
    case 'ap':
    case 'ae':
      return (value) => (value !== null ? value.toFixed(2) : '0');
    default:
      return (value) => (value !== null ? value.toFixed(0) : '0');
  }
}
